import { Message } from "./Interface";
import { PostMessageConnectorBase } from "./PostMessageConnectorBase";
import { UnitValue, InboundPartnr, PartNrMessage, AssignmentMessage, Material, SurfaceTreatment, Assignment, AlternateMaterial, InternalAlternate } from "../types/CommonTypes";
import { validatePartnrPayload } from "../utils/validatePartnrPayload";

export type MTLData = {
    name: string;
    parameters: {
        name: string;
        type: string;
        default: string | number | UnitValue;
        access: string;
    }[];
    rawMTLString?: string;
}

export const mockData: MTLData = {
  "name": "Material Name aus Header",
  "parameters": [
    {
      "name": "PTC_MATERIAL_DESCRIPTION",
      "type": "String",
      "default": "",
      "access": "Full"
    },
    {
      "name": "TEMPERATURE",
      "type": "Real",
      "default": { "value": "20.0", "unit": "C" },
      "access": "Full"
    }
  ]
}

export class MTLMessage extends Message {
    static type = "abb.creo.MTLMessage" as const
    static version = 1 as const;
    mtl?: MTLData;
    assignment?: Assignment[];
    alternates?: AlternateMaterial[];
    constructor(mtl?: MTLData){
        super();
        this.mtl = mtl;
    }
}

type InternalPartnr = {
    partNumber: string;
    bmId?: string;
    stMaterialId?: string;
    stName?: string;
    bmName?: string;
    isAssembly?: string;
    density?: string;
    coverage?: string;
    thickness?: string;
    alternates?: InternalAlternate[];
};

export class CreoConnector extends PostMessageConnectorBase {

    protected readonly name = "CreoConnector";
    protected readonly protocolVersion = 1;
    protected readonly advertisedMessageTypes = [
        { type: MTLMessage.type, version: MTLMessage.version },
        { type: PartNrMessage.type, version: PartNrMessage.version },
        { type: AssignmentMessage.type, version: AssignmentMessage.version },
    ] as const;

    protected sendDomainMessage(message: Message): void {
        if (message instanceof AssignmentMessage) {
            this.postToTarget(message.assignments)
        } else if (message instanceof MTLMessage) {
            const payload: any = { assignment: message.assignment, ...message.mtl };
            if (message.alternates) {
                payload.alternates = message.alternates.map(a => ({
                    partNr: a.partNr,
                    bmId: a.materialId,
                    bmName: a.materialName,
                    abbId: a.abbId ?? null,
                    bmDensity: a.materialDensity ?? null,
                    conditionType: a.conditionType ?? null,
                    conditionCode: a.condition?.code ?? null,
                    condition: a.condition ?? null,
                }));
            }
            this.postToTarget(payload);
        } else {
            super.sendDomainMessage(message);
        }
    }

    protected handleIncomingData(data: unknown): void {
        const parts = validatePartnrPayload<InternalPartnr>(data, this.name);
        if (parts.length === 0) {
            console.debug(`[${this.name}]: No valid part entries found in payload, ignoring.`);
            return;
        }
        const converted: InboundPartnr[] = parts.map(p => ({
            partnr: p.partNumber,
            bmId: p.bmId || undefined,
            stMaterialId: p.stMaterialId || undefined,
            bmName: p.bmName,
            isAssembly: `${p.isAssembly}`.toLowerCase() === "true",
            density: p.density,
            coverage: p.coverage,
            thickness: p.thickness,
            stName: p.stName,
            alternates: Array.isArray(p.alternates)
                ? p.alternates
                    .filter(a => a.bmId)
                    .map(a => ({
                        bmId: a.bmId!,
                        bmName: a.bmName,
                        bmDensity: a.bmDensity,
                        conditionType: (a.conditionType as 'color' | 'dimension' | null) ?? null,
                        condition: a.condition ?? (a.conditionCode ? { code: a.conditionCode } : null),
                    }))
                : undefined,
        }));
        this.notifyListeners(this.envelope(new PartNrMessage(converted)));
    }
}